import { useState } from "react";

// MUI imports
import { Alert, Card, Divider, Radio, RadioGroup, FormControlLabel } from "@mui/material";

// MUI Icons imports
import Icon from "@mui/material/Icon";
import PersonIcon from "@mui/icons-material/Person";

// Customized MUI components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Other imports
import PropTypes from "prop-types";
import {
  calculateAddonAmount,
  calculateAccommodationAmount,
  getOfferWindow,
  getHSSWindow,
  calculateHssSurcharge,
} from "./hardcodedData";
import {
  daysUntilDeparture,
  resolveApplicableInstallments,
} from "utils/installmentSchedule";
import { formatAmount } from "./TravelOrderUtility";

const GATEWAY_OPTIONS = [
  { id: "stripe", label: "Stripe (Card)" },
  { id: "razorpay", label: "Razorpay" },
];

const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";


const formatPaymentType = (type) => {
  switch (type) {
    case "full_payment": return "Full Payment";
    case "deposit_final_balance": return "Deposit + Final Balance";
    case "deposit_installments": return "Deposit + Installments";
    default: return type;
  }
};

function ReviewPaymentStep({
  selectedTripPackage,
  selectedDepartureDate,
  paymentError,
  onEditStep,
  flightPreference,
  travelers,
  selectedAccommodations,
  accommodationTravelerMap,
  selectedAddons,
  addonTravelerMap,
  isLoadingTemplates,
  templatesError,
  paymentTemplates,
  invalidPaymentTemplate,
  selectedPaymentTemplateId,
  onSelectPaymentTemplate,
  gatewayAvailability,
  isLoadingGateways,
  gatewayError,
  selectedGateway,
  onGatewayChange,
  pricing,
  bookingStatus,
  isProcessingPayment,
  onPayNow,
}) {
  const [fullPayment, setFullPayment] = useState(false);

  if (!selectedTripPackage) return null;

  return (
    <MDBox>

      {/* Header */}

      <MDTypography variant="h6" fontWeight="bold" mb={0.5}>
        Review Your Booking
      </MDTypography>
      <MDTypography variant="body2" color="text" mb={2}>
        Please review all details before proceeding to payment.
      </MDTypography>

      {paymentError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {paymentError}
        </Alert>
      )}

      {/* Trip Details Card */}

      <Card sx={{ boxShadow: "none", border: "1px solid #E8EDF5", mb: 2 }}>
        <MDBox p={2}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <MDTypography variant="button" fontWeight="bold">
              Trip Details
            </MDTypography>
            <MDButton variant="text" color="info" size="small" onClick={() => onEditStep(0)}>
              Edit
            </MDButton>
          </MDBox>
          <MDBox display="flex" gap={1.5}>
            <MDBox
                component="img"
                src={selectedTripPackage.image}
                alt={selectedTripPackage.title}
                sx={{ width: 88, height: 64, borderRadius: 1.5, objectFit: "cover", bgcolor: "#F0F3F8" }}
            />
            <MDBox flex={1}>
              <MDTypography variant="button" fontWeight="bold" display="block">
                {selectedTripPackage.title}
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block">
                {new Date(selectedDepartureDate?.departure_date).toLocaleDateString("en-IN")} -{" "}
                {new Date(selectedDepartureDate?.return_date).toLocaleDateString("en-IN")} • {selectedTripPackage.duration}
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block">
                Booking Category: {flightPreference}
              </MDTypography>
            </MDBox>
            <MDBox textAlign="right">
              <MDTypography variant="caption" color="text" display="block">
                Travelers
              </MDTypography>
              <MDTypography variant="button" fontWeight="medium">
                {travelers?.length}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>

      {/* Travelers Card */}

      <MDBox display="flex" flexWrap="wrap" gap={1.5} mb={2}>
        <MiniCard title="Travelers" onEdit={() => onEditStep(1)}>
          {travelers.map((traveler, index) => (
            <MDTypography key={traveler.id} variant="caption" color="text" display="block">
              {index + 1}. {traveler.first_name || "Unnamed traveler"}
              {traveler.isPrimary ? " (Primary)" : ""}
            </MDTypography>
          ))}
        </MiniCard>

        {/* Accommodation Card */}

        <MiniCard title="Accommodation" icon="maps_home_work" onEdit={() => onEditStep(2)}>
          {selectedAccommodations.length ? (
              selectedAccommodations.map((item) => {
                const travelerCountForRoom = (accommodationTravelerMap[item.addon.id] || []).length; // FIXED — was item.linkedService.id
                return (
                    <MDBox key={item.addon.id} display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                      <MDTypography variant="caption" color="text" sx={{ flex: 1 }}>
                        • {item.linkedService.name}
                      </MDTypography>
                      {travelerCountForRoom > 0 && (
                          <MDBox display="flex" alignItems="center" gap={0.25} sx={{ flexShrink: 0 }}>
                            <PersonIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                            <MDTypography variant="caption" color="text" fontWeight="medium">
                              {travelerCountForRoom}
                            </MDTypography>
                          </MDBox>
                      )}
                    </MDBox>
                );
              })
          ) : (
              <MDTypography variant="caption" color="text" sx={{ fontStyle: "italic", opacity: 0.65 }}>
                No accommodation selected
              </MDTypography>
          )}
        </MiniCard>

        {/* Add-on Card */}

        <MiniCard title="Add-ons" icon="celebration" onEdit={() => onEditStep(2)}>
          {selectedAddons.length ? (
              selectedAddons.map((item) => {
                const travelerCountForAddon = (addonTravelerMap[item.addon.id] || []).length;
                return (
                    <MDBox key={item.addon.id} display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                      <MDTypography variant="caption" color="text" sx={{ flex: 1 }}>
                        • {item.addon.name}
                      </MDTypography>
                      {travelerCountForAddon > 0 && (
                          <MDBox display="flex" alignItems="center" gap={0.25} sx={{ flexShrink: 0 }}>
                            <PersonIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                            <MDTypography variant="caption" color="text" fontWeight="medium">
                              {travelerCountForAddon}
                            </MDTypography>
                          </MDBox>
                      )}
                    </MDBox>
                );
              })
          ) : (
              <MDTypography variant="caption" color="text" sx={{ fontStyle: "italic", opacity: 0.65 }}>
                No add-ons selected
              </MDTypography>
          )}
        </MiniCard>

      </MDBox>

      {/* Payment Template Select Menu */}

      <Card sx={{ boxShadow: "none", border: "1px solid #E8EDF5", mb: 2 }}>
        <MDBox p={2}>
          <MDTypography variant="button" fontWeight="bold" display="block" mb={0.5}>
            Payment Plan
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block" mb={1.5}>
            Select how you'd like to pay for this trip
          </MDTypography>

          {isLoadingTemplates && (
              <MDTypography variant="caption" color="text">Loading payment plans…</MDTypography>
          )}
          {!isLoadingTemplates && templatesError && (
              <Alert severity="error">{templatesError}</Alert>
          )}
          {!isLoadingTemplates && !templatesError && paymentTemplates.length === 0 && (
              <MDTypography variant="caption" color="text">
                No payment plans are configured for this trip.
              </MDTypography>
          )}

          {!isLoadingTemplates && paymentTemplates.length > 0 && (
              <MDBox
                  sx={{
                    border: "1px solid",
                    borderColor: invalidPaymentTemplate ? "error.main" : "divider",
                    borderRadius: 2,
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
              >
                {paymentTemplates.map((template) => {
                  const isSelected = selectedPaymentTemplateId === template.id;
                  return (
                      <MDBox
                          key={template.id}
                          onClick={() => {
                            onSelectPaymentTemplate(template.id);
                            if (template.payment_type === "full_payment") {
                              setFullPayment(true);
                            } else {
                              setFullPayment(false);
                            }
                          }}
                          sx={{
                            border: "1px dashed",
                            borderColor: isSelected ? "primary.main" : "divider",
                            borderRadius: 1,
                            display: "flex",
                            gap: 1.5,
                            p: 1.5,
                            cursor: "pointer",
                          }}
                      >
                        <Radio size="small" checked={isSelected} sx={{ alignSelf: "flex-start", p: 0.5 }} />
                        <MDBox flex={1}>
                          <MDTypography fontWeight="medium" variant="body2">
                            {template.template_name}
                          </MDTypography>
                          <MDTypography variant="caption" color="text" display="block">
                            {formatPaymentType(template.payment_type)}
                          </MDTypography>

                          {template.payment_type === "full_payment" && (
                              <MDTypography variant="caption" color="text">
                                100% due at booking
                              </MDTypography>
                          )}

                          {template.payment_type === "deposit_final_balance" && (
                              <MDTypography variant="caption" color="text">
                                {template.deposit_percentage}% deposit now, balance due{" "}
                                {template.due_days_before_departure} days before departure
                              </MDTypography>
                          )}

                          {template.payment_type === "deposit_installments" && (
                              <MDBox>
                                <MDTypography variant="caption" color="text" display="block">
                                  {template.deposit_percentage}% deposit now
                                </MDTypography>
                              </MDBox>
                          )}
                        </MDBox>
                        {template.payment_type === "deposit_installments" && (
                            <MDBox flex={1}>
                              <MDTypography variant="caption" color="text" display="block">
                                Installment schedule
                                {selectedDepartureDate?.departure_date && (
                                  <> ({resolveApplicableInstallments(
                                    template.installments,
                                    daysUntilDeparture(new Date(), selectedDepartureDate.departure_date)
                                  ).length} of {template.number_of_installments || (template.installments || []).length} apply)</>
                                )}
                              </MDTypography>
                              {resolveApplicableInstallments(
                                template.installments,
                                selectedDepartureDate?.departure_date
                                  ? daysUntilDeparture(new Date(), selectedDepartureDate.departure_date)
                                  : null
                              ).map((inst) => (
                                  <MDTypography
                                      key={inst.installment_number}
                                      variant="caption"
                                      color="text"
                                      display="block"
                                      sx={{ ml: 1.5 }}
                                  >
                                    • {inst.installment_percentage}% : {inst.days_before_departure} days before departure
                                  </MDTypography>
                              ))}
                          </MDBox>
                        )}
                      </MDBox>
                  );
                })}
              </MDBox>
          )}
          {invalidPaymentTemplate && (
              <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                Please select a payment plan to continue.
              </MDTypography>
          )}
        </MDBox>
      </Card>

      {/* Payment Gateway Card (AC1 / AC2 / AC10) */}

      <MDBox display="flex" flexWrap="wrap" gap={1.5}>
        <Card sx={{ boxShadow: "none", border: "1px solid #E8EDF5", flex: "1 1 320px" }}>
          <MDBox p={2}>
            <MDTypography variant="button" fontWeight="bold" display="block" mb={0.75}>
              Payment Gateway
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block" mb={1}>
              Select Stripe or Razorpay for this travel order payment
            </MDTypography>
            {isLoadingGateways && (
              <MDTypography variant="caption" color="text">Loading payment gateways…</MDTypography>
            )}
            {gatewayError && (
              <Alert severity="error" sx={{ mb: 1 }}>{gatewayError}</Alert>
            )}
            {!isLoadingGateways && !gatewayError && (
              <>
                {!gatewayAvailability?.stripe && !gatewayAvailability?.razorpay && (
                  <Alert severity="warning">
                    No payment gateways are connected. Ask a system admin to set up Stripe or Razorpay under Settings → Payments.
                  </Alert>
                )}
                <RadioGroup
                  value={selectedGateway}
                  onChange={(e) => onGatewayChange(e.target.value)}
                  name="payment-gateway"
                >
                  {GATEWAY_OPTIONS.filter((opt) => {
                    if (opt.id === "stripe") return gatewayAvailability?.stripe;
                    if (opt.id === "razorpay") return gatewayAvailability?.razorpay;
                    return false;
                  }).map((gateway) => (
                    <FormControlLabel
                      key={gateway.id}
                      value={gateway.id}
                      control={<Radio />}
                      label={<MDTypography variant="button">{gateway.label}</MDTypography>}
                      sx={{ mb: 0.25 }}
                    />
                  ))}
                </RadioGroup>
              </>
            )}
          </MDBox>
        </Card>

        {/* Payment Summary Card */}

        <Card sx={{ boxShadow: "none", border: "1px solid #E8EDF5", flex: "1 1 320px" }}>
          <MDBox p={2}>
            <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
              Payment Summary
            </MDTypography>
            <SummaryRow label="Total Amount" value={formatAmount(pricing.total, selectedTripPackage.currency)} />
            <Divider sx={{ my: 1 }} />

            {pricing.paymentSchedule.map((row, i) => (
                <SummaryRow
                    key={i}
                    label={row.dueDate ? `${row.label} (${formatDate(row.dueDate)})` : `${row.label}`}
                    value={formatAmount(row.amount, selectedTripPackage.currency)}
                />
            ))}

            <Divider sx={{ my: 1.5 }} />
            <MDTypography variant="caption" color="text" display="block" mb={1}>
              Booking Status: {bookingStatus}
            </MDTypography>
            <MDButton
                variant="gradient"
                color="info"
                fullWidth
                disabled={
                  !selectedGateway
                  || !selectedPaymentTemplateId
                  || isProcessingPayment
                  || isLoadingGateways
                  || (!gatewayAvailability?.stripe && !gatewayAvailability?.razorpay)
                }
                onClick={() => onPayNow()}
            >
              {isProcessingPayment
                  ?  "Preparing payment link..."
                  : "Send payment link to customer"}
            </MDButton>
          </MDBox>
        </Card>
      </MDBox>
    </MDBox>
  );
}

function MiniCard({ title, children, onEdit, icon }) {
  return (
      <Card sx={{ boxShadow: "none", border: "1px solid #E8EDF5", flex: "1 1 220px" }}>
        <MDBox p={1.5}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
            <MDBox display="flex" alignItems="center" gap={0.5}>
              {icon && <Icon sx={{ fontSize: 18, color: "text.secondary" }}>{icon}</Icon>} {/* NEW */}
              <MDTypography variant="button" fontWeight="bold">
                {title}
              </MDTypography>
            </MDBox>
            <MDButton variant="text" color="info" size="small" onClick={onEdit}>
              Edit
            </MDButton>
          </MDBox>
          {children}
        </MDBox>
      </Card>
  );
}

function SummaryRow({ label, value }) {
  return (
    <MDBox display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
      <MDTypography variant="caption" color="text">
        {label}
      </MDTypography>
      <MDTypography variant="button" fontWeight="medium">
        {value}
      </MDTypography>
    </MDBox>
  );
}

export const buildReviewPricing = ({
  baseSubtotal,
  selectedAccommodations,
  accommodationTravelerMap,
  selectedAddons,
  addonTravelerMap,
  tripNights,
  paymentTemplate,
  selectedDepartureDate,
  bookingDate = new Date(),
  hssTemplate = null,
  taxes,
}) => {

  // sum each selected room, priced by its own assigned traveler count
  const accommodationAmount = selectedAccommodations.reduce((sum, accItem) => {
    const roomTravelerCount = (accommodationTravelerMap[accItem.addon.id] || []).length;
    return sum + calculateAccommodationAmount(accItem, tripNights, roomTravelerCount);
  }, 0);

  // sum each selected addon, priced by its own assigned traveler count
  const addonAmount = selectedAddons.reduce((sum, item) => {
    const addonTravelerCount = (addonTravelerMap[item.addon.id] || []).length;
    return sum + calculateAddonAmount(item.addon, addonTravelerCount);
  }, 0);

  // Evaluating total price : [ base - discount + HSS surcharge + addons + accommodation + taxes + fees ]
  const rawDiscountPercentage = selectedDepartureDate?.discount_percentage || 0;
  const offerWindow = getOfferWindow(selectedDepartureDate);
  const isDiscountLive = rawDiscountPercentage > 0 && (!offerWindow || offerWindow.isActive);
  const discountPercentage = isDiscountLive ? rawDiscountPercentage : 0;
  const discountedPrice = baseSubtotal - (baseSubtotal * discountPercentage) / 100;

  const hssWindow = getHSSWindow(hssTemplate);
  const isHssLive = Boolean(hssTemplate) && Boolean(hssWindow?.isActive);
  const hssSurchargeAmount = calculateHssSurcharge(discountedPrice, hssTemplate, selectedDepartureDate);
  const priceAfterHss = discountedPrice + hssSurchargeAmount;

  const subtotal = priceAfterHss + accommodationAmount + addonAmount;

  // Evaluating total tax amount - Configured from Settings > Company Settings > Taxes
  let total_tax_amount = 0
  for (const tax of taxes) {
    total_tax_amount += subtotal * (tax.rate / 100)
  }

  // Evaluating total payable amount.
  const total = subtotal + total_tax_amount;

  // no template selected yet : nothing is payable until the user picks one
  let amountPayable = 0;
  let remainingAmount = total;
  let paymentSchedule = [];

  const dueDateFromDays = (days) => {
    if (!selectedDepartureDate?.departure_date || days == null) return null;
    const d = new Date(selectedDepartureDate?.departure_date);
    d.setDate(d.getDate() - days);
    return d;
  };

  // IF payment template is selected

  if (paymentTemplate) {
    const depositPct = parseFloat(paymentTemplate.deposit_percentage) || 0;

    // IF payment template type is Full Payment

    if (paymentTemplate.payment_type === "full_payment") {
      amountPayable = total;
      remainingAmount = 0;
      paymentSchedule = [{ label: "Full Payment", amount: total, dueDate: null }];

    } else if (paymentTemplate.payment_type === "deposit_final_balance") {
      amountPayable = (total * depositPct) / 100;
      remainingAmount = total - amountPayable;
      paymentSchedule = [
        { label: "Deposit (Today)", amount: amountPayable, dueDate: null },
        {
          label: "Final Balance",
          amount: remainingAmount,
          dueDate: dueDateFromDays(paymentTemplate.due_days_before_departure),
        },
      ];

    } else if (paymentTemplate.payment_type === "deposit_installments") {
      amountPayable = Math.round((total * depositPct) / 100);
      const daysRemaining = daysUntilDeparture(
        bookingDate,
        selectedDepartureDate?.departure_date
      );
      const applicableInstallments = resolveApplicableInstallments(
        paymentTemplate.installments,
        daysRemaining
      );
      let pendingBalance = total - amountPayable;
      const installmentRows = applicableInstallments.map((inst, idx) => {
        const pct = parseFloat(inst.installment_percentage) || 0;
        let amount;
        if (idx === applicableInstallments.length - 1) {
          amount = pendingBalance;
        } else {
          amount = Math.round((total * pct) / 100);
          if (amount > pendingBalance) amount = pendingBalance;
        }
        pendingBalance = Math.max(0, pendingBalance - amount);
        return {
          label: `Installment ${inst.installment_number}`,
          amount,
          dueDate: dueDateFromDays(inst.days_before_departure),
        };
      });
      remainingAmount = installmentRows.reduce((s, r) => s + r.amount, 0);
      paymentSchedule = [
        { label: "Deposit (Today)", amount: amountPayable, dueDate: null },
        ...installmentRows,
      ];
    }
  }

  return {
    subtotal,
    accommodationAmount,
    addonAmount,
    hssSurchargeAmount,
    isHssLive,
    total,
    amountPayable,
    remainingAmount,
    paymentSchedule,
  };
};

ReviewPaymentStep.propTypes = {
  selectedTripPackage: PropTypes.object,
  selectedDepartureDate: PropTypes.object,
  paymentError: PropTypes.string,
  onEditStep: PropTypes.func.isRequired,
  flightPreference: PropTypes.string,
  travelers: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedAccommodations: PropTypes.arrayOf(PropTypes.object).isRequired,
  accommodationTravelerMap: PropTypes.object.isRequired,
  selectedAddons: PropTypes.arrayOf(PropTypes.object).isRequired,
  addonTravelerMap: PropTypes.object.isRequired,
  isLoadingTemplates: PropTypes.bool,
  templatesError: PropTypes.string,
  paymentTemplates: PropTypes.arrayOf(PropTypes.object),
  invalidPaymentTemplate: PropTypes.bool,
  selectedPaymentTemplateId: PropTypes.number,
  onSelectPaymentTemplate: PropTypes.func.isRequired,
  gatewayAvailability: PropTypes.shape({
    stripe: PropTypes.bool,
    razorpay: PropTypes.bool,
    currency: PropTypes.string,
  }),
  isLoadingGateways: PropTypes.bool,
  gatewayError: PropTypes.string,
  selectedGateway: PropTypes.string,
  onGatewayChange: PropTypes.func.isRequired,
  pricing: PropTypes.shape({
    total: PropTypes.number,
    amountPayable: PropTypes.number,
    remainingAmount: PropTypes.number,
  }).isRequired,
  bookingStatus: PropTypes.string.isRequired,
  isProcessingPayment: PropTypes.bool.isRequired,
  onPayNow: PropTypes.func.isRequired,
};

MiniCard.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  onEdit: PropTypes.func.isRequired,
  icon: PropTypes.string,
};

SummaryRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

export default ReviewPaymentStep;
